import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import moment from 'moment';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { GridForm } from 'components/Ui/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import { postAssociateDocument } from 'layouts/documentManagement/services/documentManagementApi';
import { getStagedEmployeeDocuments } from 'layouts/documentManagement/services/documentManagementApi';
import { setCompany } from 'layouts/documentManagement/store/employeeDocumentsFilterSlice/employeeDocumentsFilterSlice';
import { setStagedEmployeeDocuments } from 'layouts/documentManagement/store/stagedEmployeeDocumentsSlice/stagedEmployeeDocumentsSlice';
import {
  PaperPropsCheck,
  dateFormat,
  associateDocumentSuccess,
  requiredField,
} from 'constants';
import {
  getDialogHeading,
  renderSaveCancelButtons,
  refinePayload,
  useNotification,
  getSnackBar,
  dropDownValidation,
  getCompanyOptions,
} from 'utils/commonUtils';

const AssociateDocumentDialog = (props) => {
  const {
    associateDocumentDialog,
    setAssociateDocumentDialog,
    setValue,
    documentTypes,
    fetchEmployeeDocuments,
  } = props;

  const dispatch = useDispatch();

  const { handleErrorResponse, NotificationPopup } = useNotification();

  const [loadingButton, setLoadingButton] = useState(false);

  const employee = useSelector((state) => state.employee?.employee);
  const stagedEmployeeDocuments = useSelector(
    (state) => state.stagedEmployeeDocuments
  );

  const employeeDetails = useSelector((state) => state.employeeDetails);

  const { activeCompanyAssociations } = employeeDetails || {};

  const companyOptions = getCompanyOptions(activeCompanyAssociations);

  const stagedEmployeeDocumentsFilenameOptions = stagedEmployeeDocuments?.map(
    (item) => ({
      id: item.filename,
      value: item.filename,
    })
  );

  const documentTypesOptions = documentTypes?.map((item) => ({
    id: item.type,
    value: item.type.charAt(0).toUpperCase() + item.type.slice(1),
  }));

  const defaultValue = {
    company: '',
    filename: '',
    documentType: '',
    documentDate: '',
  };

  const schema = yup.object().shape({
    company: dropDownValidation,
    filename: dropDownValidation,
    documentType: dropDownValidation,
    documentDate: yup.string().when('documentType', (documentType, schema) => {
      const rules = documentTypes?.find((item) => item.type === documentType);
      if (!rules || !rules.dateAccepted) {
        return schema.strip();
      }
      if (rules.dateRequired) {
        return schema.required(requiredField);
      }
      return schema.nullable().notRequired();
    }),
  });

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    control,
    reset,
    register,
    watch,
    formState,
    clearErrors,
  } = useFunction;
  const { errors } = formState;

  const watchDocumentType = watch('documentType');
  const selectedDocTypeConfig = documentTypes?.find(
    (doc) => doc.type === watchDocumentType
  );
  const showDateField = selectedDocTypeConfig?.dateAccepted ?? false;
  const isDateRequired = selectedDocTypeConfig?.dateRequired ?? false;
  const dateLabel = isDateRequired
    ? 'Document Date'
    : 'Document Date (Optional)';

  const watchDocumentDate = watch('documentDate');

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.company,
    defaultValue.documentInStagingArea,
    defaultValue.documentType,
    defaultValue.documentDate,
  ]);

  const handleClose = () => {
    setAssociateDocumentDialog(false);
    reset(defaultValue);
  };

  const fetchStagedEmployeeDocuments = async () => {
    try {
      const stagedEmployeeDocuments = await getStagedEmployeeDocuments(
        employee
      );
      dispatch(setStagedEmployeeDocuments(stagedEmployeeDocuments?.documents));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleDocumentType = () => {
    setValue('documentDate', '');
    clearErrors();
  };

  const onSubmit = async (data) => {
    const { company, filename, documentType, documentDate } = data;

    const selectedDocTypeConfig = documentTypes.find(
      (item) => item.type === documentType
    );

    const payload = {
      filename,
      documentType,
    };

    if (selectedDocTypeConfig?.dateAccepted && watchDocumentDate) {
      payload.documentDate = moment(documentDate).format(dateFormat);
    }

    try {
      setLoadingButton(true);
      await postAssociateDocument(refinePayload(payload), employee, company);
      fetchEmployeeDocuments();
      fetchStagedEmployeeDocuments();
      setLoadingButton(false);
      const snackBar = getSnackBar(associateDocumentSuccess);
      dispatch(displaySnackbar(snackBar));
      dispatch(setCompany(company));
      setValue('company', company);
      handleClose();
    } catch (error) {
      setLoadingButton(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  return (
    <>
      <Dialog open={associateDocumentDialog} PaperProps={PaperPropsCheck}>
        {getDialogHeading('Associate Document', handleClose)}
        <GridForm container spacing={2}>
          <Grid item xs={12}>
            <FormInputDropdown
              name="company"
              control={control}
              options={companyOptions}
              label="Company"
              errors={!!errors.company}
              helperText={errors?.company?.message}
            />
            <FormInputDropdown
              name="filename"
              control={control}
              options={stagedEmployeeDocumentsFilenameOptions}
              label="Document In Staging Area"
              errors={!!errors.filename}
              helperText={errors?.filename?.message}
              noData="No staged documents found"
            />
            <FormInputDropdown
              name="documentType"
              control={control}
              options={documentTypesOptions}
              label="Document Type"
              errors={!!errors.documentType}
              helperText={errors?.documentType?.message}
              register={register('documentType', {
                onChange: (e) => handleDocumentType(e),
              })}
            />
            {showDateField && (
              <FormInputDate
                name="documentDate"
                control={control}
                label={dateLabel}
                errors={!!errors.documentDate}
                helperText={errors?.documentDate?.message}
                value={defaultValue.documentDate}
              />
            )}
            {renderSaveCancelButtons(
              handleClose,
              handleSubmit(onSubmit),
              loadingButton,
              false
            )}
          </Grid>
        </GridForm>
      </Dialog>
      <NotificationPopup />
    </>
  );
};

export default AssociateDocumentDialog;
